// Utilities for parsing reporting routes and building lookup indexes.

export const normalizePathKey = (path) => {
  if (!path) {
    return "";
  }
  const trimmed = String(path);
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
};

export const encodePathKey = (path) => normalizePathKey(path).replace(/\//g, "&+");

export const decodePathKey = (segment) => {
  if (!segment) {
    return "";
  }
  const decoded = decodeURIComponent(segment);
  return decoded.replace(/&\+/g, "/");
};

export const parseReportRoute = (pathname = "") => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2 || parts[0] !== "reports" || parts[1] !== "landing") {
    return { workspaceId: "", groupId: "", folderPath: "", reportId: "" };
  }
  const workspaceId = parts[2] || "";
  const segment = parts[3] || "";
  let groupId = "";
  let folderPath = "";
  let reportId = "";

  if (segment) {
    if (segment.includes("&+")) {
      folderPath = decodePathKey(segment);
    } else {
      groupId = segment;
    }
  }

  return { workspaceId, groupId, folderPath, reportId };
};

export const parsePowerBIReportRoute = (pathname = "") => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2 || parts[0] !== "powerBI" || parts[1] !== "report") {
    return { workspaceId: "", reportId: "" };
  }
  const segment = parts[2] || "";
  const [workspaceId, reportId] = segment.split("+");
  if (reportId) {
    return { workspaceId: workspaceId || "", reportId };
  }
  return { workspaceId: "", reportId: segment || "" };
};

export const parseStaticContentRoute = (pathname = "") => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 3 || parts[0] !== "sc" || parts[1] !== "report") {
    return { workspaceId: "", reportId: "" };
  }
  const segment = parts[2] || "";
  const plusIdx = segment.indexOf("+");
  if (plusIdx === -1) return { workspaceId: "", reportId: segment };
  return { workspaceId: segment.slice(0, plusIdx), reportId: segment.slice(plusIdx + 1) };
};

export const getRouteSelection = (pathname = "") => {
  const landing = parseReportRoute(pathname);
  const powerBI = parsePowerBIReportRoute(pathname);
  const sc = parseStaticContentRoute(pathname);
  return {
    workspaceId: landing.workspaceId || powerBI.workspaceId || sc.workspaceId,
    groupId: landing.groupId,
    folderPath: landing.folderPath,
    reportId: landing.reportId || powerBI.reportId || sc.reportId,
    isLanding: Boolean(landing.workspaceId),
    isPowerBI: Boolean(powerBI.reportId),
    isStaticContent: Boolean(sc.reportId),
  };
};

export const buildWorkspaceIndex = (workspaces = []) => {
  const byId = {};
  workspaces.forEach((ws) => {
    if (!ws) {
      return;
    }
    if (ws._id) {
      byId[ws._id] = ws;
    }
    if (ws.workspaceId) {
      byId[ws.workspaceId] = ws;
    }
  });
  return { byId };
};

export const normalizeWorkspaceId = (id, workspaces = []) => {
  if (!id) {
    return "";
  }
  const { byId } = buildWorkspaceIndex(workspaces);
  const match = byId[id];
  if (!match) {
    return id;
  }
  return match.workspaceId || match._id || id;
};

export const buildReportingIndex = (workspaces = []) => {
  const workspaceById = {};
  const nodeById = {};
  const nodeByPath = {};

  const trackNode = (node, workspace) => {
    if (!node) {
      return;
    }
    const keys = [node.id, node._id, node.rptId].filter(Boolean);
    keys.forEach((key) => {
      nodeById[key] = { node, workspace };
    });
    if (node.path) {
      const key = encodePathKey(node.path);
      nodeByPath[key] = { node, workspace };
    }
  };

  const walkNodes = (nodes, workspace) => {
    if (!Array.isArray(nodes)) {
      return;
    }
    nodes.forEach((node) => {
      trackNode(node, workspace);
      if (Array.isArray(node.children)) {
        walkNodes(node.children, workspace);
      }
      if (Array.isArray(node.activeChildren)) {
        walkNodes(node.activeChildren, workspace);
      }
    });
  };

  workspaces.forEach((ws) => {
    if (!ws) {
      return;
    }
    const key = ws.workspaceId || ws._id;
    if (key) {
      workspaceById[key] = ws;
    }
    // Walk the flat lists first so that the folder-tree entries (ws.children),
    // which carry the full "WorkspaceName/FolderName/ReportName" path, are
    // indexed LAST and therefore win when the same report id appears in both
    // ws.reports (no path) and ws.children (path set by processPBIFolders).
    walkNodes(ws.reports, ws);
    walkNodes(ws.groupings, ws);
    walkNodes(ws.fileGroupings, ws);
    walkNodes(ws.children, ws);
  });

  return { workspaceById, nodeById, nodeByPath };
};

let cachedWorkspacesRef = null;
let cachedReportingIndex = null;

export const getReportingIndex = (workspaces = []) => {
  if (cachedWorkspacesRef === workspaces && cachedReportingIndex) {
    return cachedReportingIndex;
  }
  cachedWorkspacesRef = workspaces;
  cachedReportingIndex = buildReportingIndex(workspaces);
  return cachedReportingIndex;
};

export const nodeKey = (node) => (node ? node.id || node._id || node.rptId || "" : "");

// Folder/subfolder path strings get their trailing slash trimmed inconsistently as they
// pass through the tab reducer (see topLevelNavReducer's `.replace(/\/$/, "")` calls), so
// path-based identity must be compared post-normalization, not as raw strings.
const nodePathKey = (node) => (node && node.path ? normalizePathKey(node.path) : "");

// Reports/folders accumulate in a tab's activeChildren tree as they're navigated into,
// mirroring how top-level workspace tabs accumulate in the `tabs` array — so this tree
// is the source of truth for "open" (vs merely "in view") nodes within a workspace tab.
export const collectOpenNodeKeys = (nodes) => {
  const keys = new Set();
  const walk = (list) => {
    if (!Array.isArray(list)) {
      return;
    }
    list.forEach((node) => {
      if (!node) {
        return;
      }
      const key = nodeKey(node);
      if (key) {
        keys.add(key);
      }
      const pathKey = nodePathKey(node);
      if (pathKey) {
        keys.add(pathKey);
      }
      walk(node.activeChildren);
    });
  };
  walk(nodes);
  return keys;
};

export const isNodeOpen = (node, openKeys) => {
  if (!node || !openKeys) {
    return false;
  }
  const key = nodeKey(node);
  if (key && openKeys.has(key)) {
    return true;
  }
  const pathKey = nodePathKey(node);
  return Boolean(pathKey && openKeys.has(pathKey));
};

// The breadcrumb bar's own "open" tracking (see breadcrumbReducer's stackByWorkspace)
// is a flat per-workspace array of { type: "report"|"folder"|"group", id } entries —
// folder ids are normalized paths, group ids are custom-app grouping ids, report ids
// are whatever canonical id was resolved from the URL when the report was opened.
// This is the source of truth the dropdown menu's "open" styling should match, since
// it's also what drives closing a report from the menu (see TopLevelNav.closeMenuReport).
export const isNodeOpenInStack = (node, stack) => {
  if (!node || !Array.isArray(stack) || stack.length === 0) {
    return false;
  }
  if (node.fileType === "folder" || node.type === "grouping") {
    const pathKey = nodePathKey(node);
    const idCandidates = [node.id, node._id, node.rptId].filter(Boolean);
    return stack.some(
      (s) => (s.type === "folder" || s.type === "group") && (s.id === pathKey || idCandidates.includes(s.id)),
    );
  }
  const idCandidates = [node.id, node._id, node.rptId].filter(Boolean);
  if (idCandidates.length && stack.some((s) => s.type === "report" && idCandidates.includes(s.id))) {
    return true;
  }
  const pathKey = nodePathKey(node);
  return Boolean(pathKey && stack.some((s) => s.type === "folder" && s.id === pathKey));
};

export const isRouteMatch = (node, routeSelection = {}) => {
  if (!node || !routeSelection) {
    return false;
  }
  const { groupId, folderPath, reportId } = routeSelection;
  if (reportId) {
    // Check every id-like field, not just the first non-empty one — a report can carry
    // both a PBI GUID (.id) and a Mongo _id, and the URL may have been built from
    // whichever one a different code path resolved, so only matching the first field
    // in priority order (as this used to) silently misses the other.
    const idCandidates = [node.id, node._id, node.rptId].filter(Boolean);
    if (idCandidates.includes(reportId)) {
      return true;
    }
    if (node.id && node.id.includes("+") && node.id.split("+")[1] === reportId) {
      return true;
    }
  }
  if (groupId && (node.id === groupId || node._id === groupId)) {
    return true;
  }
  if (folderPath && node.path) {
    return encodePathKey(node.path) === encodePathKey(folderPath);
  }
  return false;
};

export const getRouteContext = (pathname = "", workspaces = []) => {
  const selection = getRouteSelection(pathname);
  const index = getReportingIndex(workspaces);
  const workspace = selection.workspaceId ? index.workspaceById[selection.workspaceId] || null : null;
  let node = null;
  if (selection.folderPath) {
    node = index.nodeByPath[encodePathKey(selection.folderPath)]?.node || null;
  } else if (selection.groupId) {
    node = index.nodeById[selection.groupId]?.node || null;
  }
  const report = selection.reportId ? index.nodeById[selection.reportId]?.node || null : null;
  return { selection, workspace, node, report, index };
};

export const getRouteBreadcrumb = (pathname = "", workspaces = []) => {
  const { workspaceId, groupId, folderPath } = parseReportRoute(pathname);
  if (!workspaceId || (!groupId && !folderPath)) {
    return { workspaceId, node: null, workspace: null };
  }
  const { nodeById, nodeByPath } = buildReportingIndex(workspaces);
  if (folderPath) {
    const entry = nodeByPath[encodePathKey(folderPath)];
    return { workspaceId, node: entry ? entry.node : null, workspace: entry ? entry.workspace : null };
  }
  if (groupId) {
    const entry = nodeById[groupId];
    return { workspaceId, node: entry ? entry.node : null, workspace: entry ? entry.workspace : null };
  }
  return { workspaceId, node: null, workspace: null };
};
